/**
 * Per-topic real-world case studies - short, citation-backed stories that show
 * why the chemistry matters. Designed to be read in 3-5 minutes.
 */
export interface CaseStudy {
  title: string;
  hook: string;
  body: string[];
  takeaway: string;
  source?: { label: string; url?: string };
}

export const TOPIC_CASE_STUDIES: Record<string, CaseStudy> = {
  "lab-foundations": {
    title: "When sloppy lab notes hide a discovery",
    hook: "In 1894, Lord Rayleigh noticed nitrogen from the air was 0.5% denser than nitrogen made from chemical reactions. Most chemists would have rounded the difference away.",
    body: [
      "Rayleigh trusted his measurements, double-checked his sig figs, and worked with William Ramsay to isolate the anomaly. The 0.5% turned out to be a brand-new element - argon.",
      "Without careful notes, units, and significant-figure discipline, the discovery would have been called experimental error and discarded.",
    ],
    takeaway:
      "Good lab notebooks aren't busywork - they're the only reason small anomalies become big discoveries. Always record units, sig figs, and conditions exactly.",
    source: {
      label: "Royal Society - Rayleigh & Ramsay, 1895",
      url: "https://royalsocietypublishing.org/doi/10.1098/rspl.1894.0149",
    },
  },
  "periodic-table": {
    title: "Mendeleev's bet on the empty squares",
    hook: "When Dmitri Mendeleev published his periodic table in 1869, he left blank squares - and predicted exact properties for elements no one had ever seen.",
    body: [
      "He called one missing element 'eka-silicon' and predicted its density (5.5 g/cm³), atomic mass (~72), and oxide formula (XO₂). Sixteen years later, germanium was discovered with density 5.32 g/cm³, atomic mass 72.6, and oxide GeO₂.",
      "His arrangement worked because periodicity is real - properties really do repeat as you increase atomic number.",
    ],
    takeaway:
      "The periodic table isn't just a chart - it's a predictive model. Every trend you learn (radius, IE, EN) lets you predict element behavior before you ever touch it.",
    source: {
      label: "Royal Society of Chemistry - Mendeleev's predictions",
      url: "https://www.rsc.org/periodic-table/element/32/germanium",
    },
  },
  "atomic-structure": {
    title: "Why fireworks are colored - every shell tells a story",
    hook: "The red in a fireworks display is strontium. The green is barium. The brilliant white is magnesium. Each color is a fingerprint of an atom's electron shells.",
    body: [
      "When metal salts are heated, electrons absorb energy and jump to higher orbitals. As they fall back, they emit photons of specific wavelengths - wavelengths fixed by the spacing of those shells.",
      "Astronomers use the same principle to identify what stars are made of: spectral lines from hydrogen, helium, calcium, and iron arrive on Earth as colored bands. The Bohr/quantum model isn't abstract - it's how we read the universe.",
    ],
    takeaway:
      "Quantized energy levels mean each element emits its own signature colors. Flame tests in your lab use the exact same physics as a stellar spectrograph.",
    source: {
      label: "NASA - Spectroscopy basics",
      url: "https://imagine.gsfc.nasa.gov/science/toolbox/spectra1.html",
    },
  },
  bonding: {
    title: "Why water lifts itself up a tree",
    hook: "A redwood can pull water 100+ meters straight up against gravity, with no pump. The reason is hydrogen bonding.",
    body: [
      "Water's bent shape and large electronegativity difference make each O-H bond polar. The δ⁺ hydrogen of one molecule attracts the δ⁻ oxygen of another - a hydrogen bond.",
      "These IMFs let water molecules link into long chains. As one molecule evaporates from a leaf, it pulls the next molecule up the xylem behind it. Without hydrogen bonding, trees couldn't exist at their current scale.",
    ],
    takeaway:
      "Intermolecular forces aren't 'extra credit' - they decide boiling point, surface tension, capillary action, and whether life as we know it can exist.",
    source: {
      label: "USDA Forest Service - How trees move water",
      url: "https://www.fs.usda.gov/learn/trees",
    },
  },
  stoichiometry: {
    title: "Haber, ammonia, and feeding half the planet",
    hook: "Roughly half the nitrogen atoms in your body passed through a Haber-Bosch reactor. Stoichiometry made it possible.",
    body: [
      "In 1909 Fritz Haber demonstrated N₂ + 3 H₂ → 2 NH₃ at industrial scale. The 1:3:2 mole ratio is just stoichiometry - but applied to billions of tonnes per year, it became the foundation of synthetic fertilizer.",
      "Today around 50% of the nitrogen in human protein comes from Haber-Bosch ammonia. The same reaction also consumes ~1-2% of global energy and emits significant CO₂ - a clear case of chemistry's double-edged power.",
    ],
    takeaway:
      "Mole ratios decide whether industries are profitable, whether crops grow, and how much fossil fuel a process costs. Limiting reactant calculations are how chemists optimize all of it.",
    source: {
      label: "Nature - A century of ammonia synthesis",
      url: "https://www.nature.com/articles/ngeo325",
    },
  },
  "gas-laws": {
    title: "Why scuba divers worry about Boyle's Law",
    hook: "A diver who holds their breath while ascending from 30 m can rupture their lungs in seconds. The killer is Boyle's Law.",
    body: [
      "At 30 m underwater, ambient pressure is about 4 atm. A lungful of air at that depth is at 4× the volume it would be at the surface (P₁V₁ = P₂V₂).",
      "If a diver holds their breath and ascends, the air expands as pressure drops. By the surface the volume has quadrupled - but the lungs can't. Pulmonary barotrauma is fatal in minutes if the gas escapes into the bloodstream.",
      "Dive tables, decompression stops, and 'never hold your breath' rules are all derived from the gas laws.",
    ],
    takeaway:
      "Gas laws are not just word problems - they're life-and-death rules for medicine, aviation, and diving. Always check whether the volume can physically expand.",
    source: {
      label: "NOAA Diving Manual - Gas laws and barotrauma",
      url: "https://www.omao.noaa.gov/learn/diving-program",
    },
  },
  solutions: {
    title: "Flint, Michigan - when solubility chemistry failed a city",
    hook: "In 2014 Flint switched its drinking water source. Within months, lead levels in children's blood doubled.",
    body: [
      "The new water source (the Flint River) was more corrosive - lower pH and higher chloride. Without proper corrosion-control treatment (orthophosphate), the protective lead-phosphate scale inside old pipes dissolved.",
      "Lead is barely soluble under normal conditions, but the new water shifted the solubility equilibrium. Soluble Pb²⁺ then leached into homes by the part-per-billion.",
      "It was a solubility/equilibrium problem disguised as an infrastructure problem.",
    ],
    takeaway:
      "Solubility isn't a fixed property of a substance - it depends on pH, temperature, and other ions present. Public health systems rely on chemists who understand that.",
    source: {
      label: "EPA - Flint Drinking Water Response",
      url: "https://www.epa.gov/flint",
    },
  },
  "acid-base": {
    title: "Ocean acidification - a giant titration in slow motion",
    hook: "The world's oceans have absorbed about 30% of the CO₂ humans have emitted since 1850. The result: surface ocean pH has dropped from 8.21 to 8.10.",
    body: [
      "CO₂ + H₂O ⇌ H₂CO₃ ⇌ H⁺ + HCO₃⁻. Adding CO₂ to seawater shifts the equilibrium and releases H⁺ ions, lowering pH.",
      "Because pH is logarithmic, a 0.11-unit drop is roughly a 30% increase in [H⁺]. That extra acidity dissolves the calcium-carbonate shells of plankton, corals, and shellfish - the base of marine food chains.",
      "It is, in effect, a planetary-scale acid-base reaction - and exactly the kind of equilibrium calculation students do in class.",
    ],
    takeaway:
      "pH is logarithmic, so 'small' shifts have huge biological consequences. Acid-base chemistry isn't only in beakers - it's reshaping the oceans.",
    source: {
      label: "NOAA - Ocean Acidification",
      url: "https://www.noaa.gov/education/resource-collections/ocean-coasts/ocean-acidification",
    },
  },
  thermochemistry: {
    title: "Hand warmers, ice packs, and the energy hidden in dissolution",
    hook: "A single-use hand warmer and a single-use cold pack look almost identical. Both rely on dissolution - but in opposite directions.",
    body: [
      "Hand warmer (CaCl₂ or MgSO₄ + water): exothermic dissolution, ΔH < 0. Heat is released to your hand. ΔT can exceed +30 °C.",
      "Cold pack (NH₄NO₃ + water): endothermic dissolution, ΔH > 0. Heat is absorbed from your skin. ΔT can drop below 0 °C.",
      "The same equation q = mcΔT predicts both effects, given the dissolved mass and the heat of solution.",
    ],
    takeaway:
      "Thermochemistry isn't just calorimetry homework - it's the design principle behind a $2 first-aid product. Sign of ΔH determines whether a process heats or cools its surroundings.",
    source: {
      label: "American Chemical Society - Hot and cold packs",
      url: "https://www.acs.org/education/resources/highschool/chemmatters.html",
    },
  },
  nuclear: {
    title: "Carbon-14 dating and the age of the Dead Sea Scrolls",
    hook: "When the Dead Sea Scrolls were found in the 1940s, scholars argued for decades over whether they were Roman-era forgeries. Half-life math settled it.",
    body: [
      "Living things keep a constant ratio of ¹⁴C to ¹²C from the atmosphere. When they die, ¹⁴C decays with a half-life of 5,730 years. Measuring the remaining fraction gives the death date.",
      "Linen wrappings from the scrolls showed about 75% of original ¹⁴C activity, dating them to roughly the 2nd century BCE - consistent with the Hebrew/Aramaic paleography.",
      "The same exponential N = N₀(½)^(t/T) you use in homework dated artifacts that reshaped religious history.",
    ],
    takeaway:
      "Half-life math turns a single radioactivity measurement into an absolute age. Nuclear chemistry is the foundation of archaeology, geology, and medical imaging.",
    source: {
      label: "Radiocarbon - Bonani et al., dating the Dead Sea Scrolls",
      url: "https://doi.org/10.1017/S0033822200013333",
    },
  },
};